/**
  * @class melePopup
  * @description
  *
  * # Purpose
  * Implements `<mele-popup>` custom web element.
  *
  * # Use
  * Create element in HTML:
  * ```
  * parentEl.innerHTML += '<mele-popup></mele-popup>';
  * ```
  * Create element in JavaScript:
  * ```
  * parentEl.appendChildren({
  *   tag: 'mele-popup',
  *   ... other attributes ...
  * })
  * ```
  *
  * # Implementation notes
  *
  */

import meleView from 'meleView'

const PROPERTIES = _.extend({}, {
  outclick: { type: 'string', description: "what to do when user clicks on dimmed area outside the modal; if set to ':cancel', will close the modal window, if set to 'none' will forward clicks to layers below, otherwise if set, will invoke .publish('mmButtonClicked',{key:this.outclick}) " },
  position: { type: 'string', description: "position of the modal content; 'over' will be placed to cover calling element, 'below' will be placed just below bottom edge of calling component", enum: ['full', 'center', 'right', 'left', 'top', 'bottom', 'over', 'below', 'leftto'] },
  invoker: { type: 'element', description: 'invoking element', noattribute: true },
  esc: { type: 'boolean', description: 'allows to close element with esc button', default: true },
}, meleView._properties)

export default class melePopup extends meleView {
  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Creates internal DOM structure.
  */
  init() {
    super.init()
    this.reposition()
    window.addEventListener('keydown', this.escPressed.bind(this), true)
  } // init

  disconnectedCallback() {
    if(this.isParkedDisconnect && this.isParkedDisconnect()) return
    window.removeEventListener('keydown', this.escPressed.bind(this), true)
  }

  escPressed(e) {
    if(!this.esc) return
    const parent = this.closest('.mele-window')
    if(e.keyCode === 27 && parent && parent.focused) { this.eventCancel(this) }
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * call eventClose before delete
  */
  deleteElement() {
    if(typeof this.eventClose === 'function') {
      this.eventClose(this)
    }
    super.deleteElement()
  } // deleteElement

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Recalculate position using this.invoker element's position.
  * Relevant only for position attribute set to 'over' or 'below'
  */
  reposition() {
    const position = _.toString(this.position)
    const width = parseInt(this.width)||0
    const height = parseInt(this.height)||0
    if(!this.invoker) return

    const def = {
      position: 'absolute',
      top: '',
      bottom: '',
      left: '',
      right: '',
      width: '',
      height: '',
      maxHeight: '',
      borderTopColor: '',
      borderBottomColor: '',
    }
    if(position=='left') {
      // get offset of invoking element from nearest view
      const parent = this.invoker.closest('.mele-view')
      const rect_invoker = this.invoker.getBoundingClientRect()
      const rect_parent = parent.getBoundingClientRect()
      _.extend(def, {
        top: (rect_invoker.top - rect_parent.top) + 'px',
        right: (rect_parent.width - (rect_invoker.left-rect_parent.left)) + 'px',
      })
    }else if(position=='full') {
      _.extend(def, {
        top: '0px',
        bottom: '0px',
        left: '0px',
        right: '0px',
      })
    }

    _.extend(this.style, def)
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "width"
  */
  invokerChanged() { this.reposition() }

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "width"
  */
  widthChanged() { this.reposition() }

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "height"
  */
  heightChanged() { this.reposition() }

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "position"
  */
  positionChanged() { this.reposition() }

  eventCancel(self) {
    this.popupClose()
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle click outside popup window
  */
  onclick(event) {
    if(event.target===this && this.outclick) {
      if(this.outclick==':modal.cancel') {
        this.eventCancel(this)
      }else {
        this.publish('mmButtonClicked', { holder: this.key, button: this.outclick })
      }
    } else if(this.inclick==':modal.cancel') {
      this.popupClose()
    }
  }

  formModalclose() {
    this.eventCancel(this)
  }
} // class melePopup

melePopup._properties = PROPERTIES
melePopup.observedAttributes = _.keys(PROPERTIES)

window.customElements.define('mele-popup', melePopup)
